<script lang="ts" setup>
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import { useClientHandle } from "@urql/vue";
import gql from "graphql-tag";
import { VueDatePicker } from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import { enUS, de, nl, fr } from "date-fns/locale";
import type { Locale } from "date-fns";

import { LoadingSpinner, Thumbnail } from "@/components";
import {
  DatetimeRangeInput,
  type DocumentQuery,
  IntervalInput,
  PageType,
  useDocumentQuery,
} from "@/graphql/operations";
import { Formatter } from "@/util/format";
import { presentationAnalyticsOriginState } from "@/views/analytics/presentationAnalyticsNavigation";
import PresentationAnalyticsChart from "./PresentationAnalyticsChart.vue";
import {
  buildDatetimeRangeInput,
  buildPaginationItems,
  buildPagePerformanceRows,
  buildPerformanceBuckets,
  formatDateQueryValue,
  getPaginationPageCount,
  getPresentationAnalyticsStepInput,
  hasPresentationAnalyticsAccess,
  paginateItems,
  resolvePresentationAnalyticsRange,
  resolvePresentationAnalyticsView,
  sortPagePerformanceRows,
  summarizePerformance,
  type PageSortKey,
  type PagePerformanceRow,
  type PerformanceBucket,
  type PresentationAnalyticsPreset,
  type PresentationAnalyticsView,
  type SortDirection,
} from "./presentationAnalytics";

const PresentationAnalyticsSummaryDocument = gql`
  query PresentationAnalyticsSummary(
    $id: BigInt!
    $timeFrame: DatetimeRangeInput!
    $step: IntervalInput!
    $skipShortPageViews: IntervalInput
    $labels: [String!]!
  ) {
    document(id: $id) {
      id
      presentationHcpMetrics(
        timeFrame: $timeFrame
        labels: $labels
        skipViewsIfShorterThan: $skipShortPageViews
        first: 1
      ) {
        nodes {
          totalPresentations
          presentationsWithHcp
          totalHcpAssignments
        }
      }
      viewsInPresentationsOverTime(
        timeFrame: $timeFrame
        step: $step
        skipViewsIfShorterThan: $skipShortPageViews
        labels: $labels
      ) {
        nodes {
          startsAt
          endsAt
          nViews
        }
      }
      avgWatchTimeInPresentationsOverTime(
        timeFrame: $timeFrame
        step: $step
        skipViewsIfShorterThan: $skipShortPageViews
        labels: $labels
      ) {
        nodes {
          startsAt
          endsAt
          avgWatchTimeInSeconds
        }
      }
    }
  }
`;

const PresentationAnalyticsPagesDocument = gql`
  query PresentationAnalyticsPages($id: BigInt!, $first: Int!, $after: Cursor) {
    document(id: $id) {
      id
      documentRevisionByIdAndCurrentRevisionNumber {
        documentPagesByDocumentIdAndRevisionNumber(
          first: $first
          after: $after
          orderBy: PAGE_NUMBER_ASC
        ) {
          nodes {
            id
            pageNumber
            pageType
            crossRevId
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
  }
`;

const PresentationAnalyticsHeatmapDocument = gql`
  query PresentationAnalyticsHeatmap(
    $id: BigInt!
    $timeFrame: DatetimeRangeInput!
    $skipShortPageViews: IntervalInput
    $labels: [String!]!
    $first: Int!
    $after: Cursor
  ) {
    document(id: $id) {
      id
      pageHeatmap(
        first: $first
        after: $after
        orderBy: PAGE_NUMBER_ASC
        timeFrame: $timeFrame
        skipViewsIfShorterThan: $skipShortPageViews
        labels: $labels
      ) {
        nodes {
          pageNumber
          crossRevId
          nVisits
          avgDuration
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`;

interface AnalyticsDocumentPageNode {
  id: string;
  pageNumber: number;
  pageType: PageType;
  crossRevId: string;
}

interface AnalyticsHeatmapNode {
  pageNumber?: number | null;
  crossRevId?: string | null;
  nVisits?: number | string | null;
  avgDuration?: number | string | null;
}

interface PresentationAnalyticsSummaryQueryData {
  document?: {
    id: string;
    presentationHcpMetrics?: {
      nodes: Array<{
        totalPresentations: string;
        presentationsWithHcp: string;
        totalHcpAssignments: string;
      } | null>;
    } | null;
    viewsInPresentationsOverTime?: {
      nodes: Array<{
        startsAt?: string | null;
        endsAt?: string | null;
        nViews?: number | string | null;
      } | null>;
    } | null;
    avgWatchTimeInPresentationsOverTime?: {
      nodes: Array<{
        startsAt?: string | null;
        endsAt?: string | null;
        avgWatchTimeInSeconds?: number | string | null;
      } | null>;
    } | null;
  } | null;
}

interface PresentationAnalyticsPagesQueryData {
  document?: {
    id: string;
    documentRevisionByIdAndCurrentRevisionNumber?: {
      documentPagesByDocumentIdAndRevisionNumber: {
        nodes: Array<AnalyticsDocumentPageNode | null>;
        pageInfo?: {
          hasNextPage?: boolean | null;
          endCursor?: string | null;
        } | null;
      };
    } | null;
  } | null;
}

interface PresentationAnalyticsHeatmapQueryData {
  document?: {
    id: string;
    pageHeatmap?: {
      nodes: Array<AnalyticsHeatmapNode | null>;
      pageInfo?: {
        hasNextPage?: boolean | null;
        endCursor?: string | null;
      } | null;
    } | null;
  } | null;
}

interface PresentationAnalyticsSummaryQueryVariables {
  id: string;
  timeFrame: DatetimeRangeInput;
  step: IntervalInput;
  skipShortPageViews?: IntervalInput | null;
  labels: string[];
}

interface PresentationAnalyticsPagesQueryVariables {
  id: string;
  first: number;
  after?: string | null;
}

interface PresentationAnalyticsHeatmapQueryVariables {
  id: string;
  timeFrame: DatetimeRangeInput;
  skipShortPageViews?: IntervalInput | null;
  labels: string[];
  first: number;
  after?: string | null;
}

const props = defineProps<{
  validatedDocument?: NonNullable<DocumentQuery["document"]> | null;
}>();

const route = useRoute();
const router = useRouter();
const store = useStore();
const clientHandle = useClientHandle();
const { t, locale } = useI18n();
const documentId = computed(() => route.params.documentId as string);
const PAGE_ANALYTICS_BATCH_SIZE = 250;
const skipShortPageViews: IntervalInput = { seconds: 2 };

const dfnsLocale = computed<Locale>(() => {
  const localeMap: Record<string, Locale> = { en: enUS, de, nl, fr };
  return localeMap[locale.value] ?? enUS;
});

const selectedView = computed<PresentationAnalyticsView>(() =>
  resolvePresentationAnalyticsView(route.query.view),
);

const selectedLabel = computed(() =>
  route.query.labels === "presentation" || route.query.labels === "self-study"
    ? route.query.labels
    : "both",
);
const selectedLabels = computed(() =>
  selectedLabel.value === "both"
    ? ["presentation", "self-study"]
    : [selectedLabel.value],
);

const selectedRange = computed(() =>
  resolvePresentationAnalyticsRange({
    preset: route.query.preset,
    from: route.query.from,
    to: route.query.to,
  }),
);

const selectedPreset = computed<PresentationAnalyticsPreset>(
  () => selectedRange.value.preset,
);

const documentQueryVariables = computed(() => ({
  id: documentId.value,
}));

const validatedDocument = computed(() => {
  const currentDocument = props.validatedDocument ?? null;

  return currentDocument?.id === documentId.value ? currentDocument : null;
});

const {
  data: documentData,
  error: documentError,
  fetching: fetchingDocument,
  stale: staleDocument,
} = useDocumentQuery({
  variables: documentQueryVariables,
  requestPolicy: "network-only",
  pause: computed(() => Boolean(validatedDocument.value)),
});

const document = computed(() => {
  if (validatedDocument.value) {
    return validatedDocument.value;
  }

  const currentDocument = documentData.value?.document ?? null;

  return currentDocument?.id === documentId.value ? currentDocument : null;
});

const canAccessAnalytics = computed(() =>
  hasPresentationAnalyticsAccess({
    hasCurrentAccessResult:
      Boolean(validatedDocument.value) ||
      (!fetchingDocument.value && !staleDocument.value && !documentError.value),
    hasPresentationAnalytics: store.getters.user.hasPresentationAnalytics(),
    mayAdministrateRoot: Boolean(
      document.value?.rootWorkspace?.myPrivileges?.mayAdministrate,
    ),
    deletedAt: document.value?.deletedAt,
    mimeType: document.value?.mimeType,
    numNormalPages: document.value?.numNormalPages,
  }),
);

const shouldFetchAnalytics = computed(
  () => Boolean(document.value) && canAccessAnalytics.value,
);

const analyticsDocument = ref<
  PresentationAnalyticsSummaryQueryData["document"] | null
>(null);
const fetchingAnalytics = ref(false);
const analyticsError = ref<unknown | null>(null);
const pageNodes = ref<AnalyticsDocumentPageNode[]>([]);
const pageHeatmapNodes = ref<AnalyticsHeatmapNode[]>([]);
const fetchingPageData = ref(false);
const pageDataError = ref<unknown | null>(null);
let activeAnalyticsRequestId = 0;

const pageFetchRangeKey = computed(
  () =>
    `${formatDateQueryValue(selectedRange.value.from)}:${formatDateQueryValue(
      selectedRange.value.to,
    )}`,
);

const performanceBuckets = computed<PerformanceBucket[]>(() =>
  buildPerformanceBuckets(
    (analyticsDocument.value?.viewsInPresentationsOverTime?.nodes ?? []).filter(
      Boolean,
    ) as NonNullable<
      NonNullable<
        NonNullable<
          PresentationAnalyticsSummaryQueryData["document"]
        >["viewsInPresentationsOverTime"]
      >["nodes"][number]
    >[],
    (
      analyticsDocument.value?.avgWatchTimeInPresentationsOverTime?.nodes ?? []
    ).filter(Boolean) as NonNullable<
      NonNullable<
        NonNullable<
          PresentationAnalyticsSummaryQueryData["document"]
        >["avgWatchTimeInPresentationsOverTime"]
      >["nodes"][number]
    >[],
  ),
);

const pagePerformanceRows = computed<PagePerformanceRow[]>(() =>
  buildPagePerformanceRows(pageNodes.value, pageHeatmapNodes.value),
);

const performanceSummary = computed(() =>
  summarizePerformance(performanceBuckets.value, pagePerformanceRows.value),
);

watch(
  [shouldFetchAnalytics, documentId, pageFetchRangeKey, selectedLabel],
  async ([shouldFetch]) => {
    const requestId = ++activeAnalyticsRequestId;

    if (!shouldFetch) {
      analyticsDocument.value = null;
      pageNodes.value = [];
      pageHeatmapNodes.value = [];
      fetchingAnalytics.value = false;
      fetchingPageData.value = false;
      analyticsError.value = null;
      pageDataError.value = null;
      return;
    }

    const requestedDocumentId = documentId.value;
    const requestedRange = {
      from: new Date(selectedRange.value.from),
      to: new Date(selectedRange.value.to),
      preset: selectedRange.value.preset,
    };
    const requestedTimeFrame = buildDatetimeRangeInput(requestedRange);
    const requestedStep = getPresentationAnalyticsStepInput(requestedRange);
    const requestedLabels = [...selectedLabels.value];

    analyticsDocument.value = null;
    pageNodes.value = [];
    pageHeatmapNodes.value = [];
    fetchingAnalytics.value = true;
    fetchingPageData.value = true;
    analyticsError.value = null;
    pageDataError.value = null;

    try {
      const [summaryDocument, allPageNodes, allHeatmapNodes] =
        await Promise.all([
          fetchSummaryDocument(
            requestId,
            requestedDocumentId,
            requestedTimeFrame,
            requestedStep,
            requestedLabels,
          ),
          fetchAllPageNodes(requestId, requestedDocumentId),
          fetchAllHeatmapNodes(
            requestId,
            requestedDocumentId,
            requestedTimeFrame,
            requestedLabels,
          ),
        ]);

      if (requestId !== activeAnalyticsRequestId) {
        return;
      }

      analyticsDocument.value =
        summaryDocument?.id === requestedDocumentId ? summaryDocument : null;
      pageNodes.value = allPageNodes;
      pageHeatmapNodes.value = allHeatmapNodes;
      fetchingAnalytics.value = false;
      fetchingPageData.value = false;
    } catch (error) {
      if (requestId !== activeAnalyticsRequestId) {
        return;
      }

      analyticsDocument.value = null;
      pageNodes.value = [];
      pageHeatmapNodes.value = [];
      fetchingAnalytics.value = false;
      fetchingPageData.value = false;
      analyticsError.value = error;
      pageDataError.value = null;
    }
  },
  { immediate: true },
);

const isLoading = computed(
  () =>
    fetchingDocument.value ||
    staleDocument.value ||
    (shouldFetchAnalytics.value &&
      (fetchingAnalytics.value || fetchingPageData.value)),
);

const chartMetric = ref<"views" | "watchTime">("views");

const hcpMetrics = computed(
  () => analyticsDocument.value?.presentationHcpMetrics?.nodes[0],
);
const formatCount = (value: string | undefined) =>
  value === undefined
    ? "–"
    : new Intl.NumberFormat(locale.value).format(BigInt(value));

const numberFormatter = computed(
  () => new Intl.NumberFormat(locale.value, { maximumFractionDigits: 0 }),
);

const shortDateFormatter = computed(
  () =>
    new Intl.DateTimeFormat(locale.value, {
      month: "short",
      day: "numeric",
    }),
);

const mediumDateFormatter = computed(
  () =>
    new Intl.DateTimeFormat(locale.value, {
      month: "short",
      year: "numeric",
    }),
);

const rangeDateFormatter = computed(
  () =>
    new Intl.DateTimeFormat(locale.value, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }),
);

const datePickerFormat = computed(() => {
  const localePrefix = locale.value.split("-")[0];

  return localePrefix === "en" ? "MM/dd/yyyy" : "dd.MM.yyyy";
});

const selectedRangeText = computed(
  () =>
    `${rangeDateFormatter.value.format(
      selectedRange.value.from,
    )} - ${rangeDateFormatter.value.format(selectedRange.value.to)}`,
);

const chartPoints = computed(() =>
  performanceBuckets.value.map((bucket) => ({
    label: formatChartBucketLabel(bucket),
    value:
      chartMetric.value === "views"
        ? bucket.views
        : bucket.avgWatchTimeInSeconds,
  })),
);

const drilldownRows = computed(() =>
  performanceBuckets.value.map((bucket) => ({
    label: formatDrilldownLabel(bucket),
    views: bucket.views,
    avgWatchTimeInSeconds: bucket.avgWatchTimeInSeconds,
  })),
);

const pageSortKey = ref<PageSortKey>("pageNumber");
const pageSortDirection = ref<SortDirection>("asc");
const pageSize = ref(100);
const currentTablePage = ref(1);
const pageSizeOptions = [50, 100, 200] as const;

const sortedPageRows = computed(() =>
  sortPagePerformanceRows(
    pagePerformanceRows.value,
    pageSortKey.value,
    pageSortDirection.value,
  ),
);

const totalTablePages = computed(() =>
  getPaginationPageCount(sortedPageRows.value.length, pageSize.value),
);

const visiblePageRows = computed(() =>
  paginateItems(sortedPageRows.value, currentTablePage.value, pageSize.value),
);

const paginationItems = computed(() =>
  buildPaginationItems(totalTablePages.value, currentTablePage.value),
);

const maxPageViews = computed(() =>
  Math.max(...pagePerformanceRows.value.map((row) => row.views), 0),
);

const customDateRange = computed({
  get: () => [selectedRange.value.from, selectedRange.value.to],
  set: (value: Date[] | null) => {
    if (!value || value.length !== 2 || !value[0] || !value[1]) {
      return;
    }

    updateAnalyticsQuery({
      preset: "custom",
      from: formatDateQueryValue(value[0]),
      to: formatDateQueryValue(value[1]),
    });
  },
});

watch(
  [documentId, pageFetchRangeKey, pageSize],
  () => {
    currentTablePage.value = 1;
  },
  { immediate: true },
);

watch(totalTablePages, (nextTotalPages) => {
  currentTablePage.value = Math.min(currentTablePage.value, nextTotalPages);
});

async function fetchSummaryDocument(
  requestId: number,
  requestedDocumentId: string,
  requestedTimeFrame: DatetimeRangeInput,
  requestedStep: IntervalInput,
  requestedLabels: string[],
): Promise<PresentationAnalyticsSummaryQueryData["document"] | null> {
  if (requestId !== activeAnalyticsRequestId) {
    return null;
  }

  const response = (await clientHandle.client
    .query<
      PresentationAnalyticsSummaryQueryData,
      PresentationAnalyticsSummaryQueryVariables
    >(
      PresentationAnalyticsSummaryDocument,
      {
        id: requestedDocumentId,
        timeFrame: requestedTimeFrame,
        step: requestedStep,
        labels: requestedLabels,
        skipShortPageViews,
      },
      { requestPolicy: "network-only" },
    )
    .toPromise()) as {
    data?: PresentationAnalyticsSummaryQueryData;
    error?: unknown;
  };

  if (requestId !== activeAnalyticsRequestId) {
    return null;
  }

  if (response.error) {
    throw response.error;
  }

  const result = response.data?.document;
  if (!result?.presentationHcpMetrics?.nodes[0])
    throw new Error("Missing presentation metrics");
  return result;
}

async function fetchAllPageNodes(
  requestId: number,
  requestedDocumentId: string,
): Promise<AnalyticsDocumentPageNode[]> {
  const nodes: AnalyticsDocumentPageNode[] = [];
  let after: string | null = null;

  while (true) {
    if (requestId !== activeAnalyticsRequestId) {
      return [];
    }

    const response = (await clientHandle.client
      .query<
        PresentationAnalyticsPagesQueryData,
        PresentationAnalyticsPagesQueryVariables
      >(
        PresentationAnalyticsPagesDocument,
        {
          id: requestedDocumentId,
          first: PAGE_ANALYTICS_BATCH_SIZE,
          after,
        },
        { requestPolicy: "network-only" },
      )
      .toPromise()) as {
      data?: PresentationAnalyticsPagesQueryData;
      error?: unknown;
    };

    if (requestId !== activeAnalyticsRequestId) {
      return [];
    }

    if (response.error) {
      throw response.error;
    }

    const connection =
      response.data?.document?.documentRevisionByIdAndCurrentRevisionNumber
        ?.documentPagesByDocumentIdAndRevisionNumber;

    nodes.push(
      ...((connection?.nodes ?? []).filter(
        Boolean,
      ) as AnalyticsDocumentPageNode[]),
    );

    if (!connection?.pageInfo?.hasNextPage || !connection.pageInfo.endCursor) {
      return nodes;
    }

    after = connection.pageInfo.endCursor;
  }
}

async function fetchAllHeatmapNodes(
  requestId: number,
  requestedDocumentId: string,
  requestedTimeFrame: DatetimeRangeInput,
  requestedLabels: string[],
): Promise<AnalyticsHeatmapNode[]> {
  const nodes: AnalyticsHeatmapNode[] = [];
  let after: string | null = null;

  while (true) {
    if (requestId !== activeAnalyticsRequestId) {
      return [];
    }

    const response = (await clientHandle.client
      .query<
        PresentationAnalyticsHeatmapQueryData,
        PresentationAnalyticsHeatmapQueryVariables
      >(
        PresentationAnalyticsHeatmapDocument,
        {
          id: requestedDocumentId,
          timeFrame: requestedTimeFrame,
          skipShortPageViews,
          labels: requestedLabels,
          first: PAGE_ANALYTICS_BATCH_SIZE,
          after,
        },
        { requestPolicy: "network-only" },
      )
      .toPromise()) as {
      data?: PresentationAnalyticsHeatmapQueryData;
      error?: unknown;
    };

    if (requestId !== activeAnalyticsRequestId) {
      return [];
    }

    if (response.error) {
      throw response.error;
    }

    const connection = response.data?.document?.pageHeatmap;

    nodes.push(
      ...((connection?.nodes ?? []).filter(Boolean) as AnalyticsHeatmapNode[]),
    );

    if (!connection?.pageInfo?.hasNextPage || !connection.pageInfo.endCursor) {
      return nodes;
    }

    after = connection.pageInfo.endCursor;
  }
}

function updateAnalyticsQuery(
  updates: Partial<
    Record<"view" | "preset" | "from" | "to" | "labels", string | undefined>
  >,
) {
  const nextQuery = {
    ...route.query,
  } as Record<string, string>;

  for (const [key, value] of Object.entries(updates)) {
    if (value === undefined) {
      delete nextQuery[key];
      continue;
    }

    nextQuery[key] = value;
  }

  router.replace({
    name: route.name ?? "DocumentPresentationAnalytics",
    params: route.params,
    query: nextQuery,
    state: presentationAnalyticsOriginState(window.history.state),
  });
}

function selectView(view: PresentationAnalyticsView) {
  updateAnalyticsQuery({ view });
}

function selectPreset(preset: PresentationAnalyticsPreset) {
  if (preset === "custom") {
    updateAnalyticsQuery({
      preset,
      from: formatDateQueryValue(selectedRange.value.from),
      to: formatDateQueryValue(selectedRange.value.to),
    });
    return;
  }

  updateAnalyticsQuery({
    preset,
    from: undefined,
    to: undefined,
  });
}

function toggleSort(key: PageSortKey) {
  if (pageSortKey.value === key) {
    pageSortDirection.value =
      pageSortDirection.value === "asc" ? "desc" : "asc";
    return;
  }

  pageSortKey.value = key;
  pageSortDirection.value = key === "pageNumber" ? "asc" : "desc";
}

function goToTablePage(pageNumber: number) {
  currentTablePage.value = Math.min(
    Math.max(pageNumber, 1),
    totalTablePages.value,
  );
}

function sortIcon(key: PageSortKey): string {
  if (pageSortKey.value !== key) {
    return "swap_vert";
  }

  return pageSortDirection.value === "asc" ? "north" : "south";
}

function formatMetricValue(value: number): string {
  return chartMetric.value === "views"
    ? numberFormatter.value.format(Math.round(value))
    : formatDuration(value);
}

function formatViews(value: number): string {
  return numberFormatter.value.format(value);
}

function formatDuration(value: number): string {
  return Formatter.formatDuration(Math.max(Math.round(value), 0));
}

function formatChartBucketLabel(bucket: PerformanceBucket): string {
  const startsAt = new Date(bucket.startsAt);

  if (selectedPreset.value === "12m" || selectedPreset.value === "ytd") {
    return mediumDateFormatter.value.format(startsAt);
  }

  return shortDateFormatter.value.format(startsAt);
}

function formatDrilldownLabel(bucket: PerformanceBucket): string {
  const startsAt = new Date(bucket.startsAt);
  const endsAt = new Date(new Date(bucket.endsAt).getTime() - 1);

  const sameMonth =
    startsAt.getMonth() === endsAt.getMonth() &&
    startsAt.getFullYear() === endsAt.getFullYear();

  if (selectedPreset.value === "12m") {
    return mediumDateFormatter.value.format(startsAt);
  }

  if (sameMonth) {
    return `${shortDateFormatter.value.format(startsAt)} - ${endsAt.getDate()}`;
  }

  return `${shortDateFormatter.value.format(startsAt)} - ${shortDateFormatter.value.format(
    endsAt,
  )}`;
}

function pageBarWidth(row: PagePerformanceRow): string {
  if (maxPageViews.value <= 0 || row.views <= 0) {
    return "0%";
  }

  return `${Math.max((row.views / maxPageViews.value) * 100, 4)}%`;
}

function pageChipStyle(row: PagePerformanceRow) {
  const opacity =
    maxPageViews.value > 0
      ? Math.max(row.views / maxPageViews.value, 0.08)
      : 0.08;

  return {
    backgroundColor: `color-mix(in srgb, var(--classic-accent-soft, #35b6b9) ${opacity * 100}%, transparent)`,
    color:
      row.views > 0
        ? "var(--classic-text, #004D50)"
        : "var(--classic-text-muted, #6B7280)",
  };
}

onBeforeUnmount(() => {
  activeAnalyticsRequestId += 1;
});
</script>

<template>
  <div
    v-if="document && canAccessAnalytics"
    class="presentation-analytics-page tw:font-sans tw:-mx-6 tw:-mb-6"
    data-test="presentation-analytics"
  >
    <div class="presentation-analytics-container">
      <div class="presentation-analytics-hero">
        <div class="presentation-analytics-hero-copy">
          <div class="presentation-analytics-eyebrow">
            {{ t("presentationAnalytics.title") }}
          </div>
          <h2 class="presentation-analytics-title">
            {{ document.displayTitle }}
          </h2>
          <p class="presentation-analytics-subtitle">
            {{ t("presentationAnalytics.subtitle") }}
          </p>
        </div>

        <div class="presentation-analytics-range-panel">
          <label class="presentation-analytics-label-filter">
            <span>{{ t("presentationAnalytics.hcps.labelFilter") }}</span>
            <select
              :value="selectedLabel"
              data-test="analytics-label-filter"
              @change="
                updateAnalyticsQuery({
                  labels: ($event.target as HTMLSelectElement).value,
                })
              "
            >
              <option value="both">
                {{ t("presentationAnalytics.hcps.both") }}
              </option>
              <option value="presentation">
                {{ t("documentHistory.label.presentation.title") }}
              </option>
              <option value="self-study">
                {{ t("documentHistory.label.self-study.title") }}
              </option>
            </select>
          </label>
          <div class="presentation-analytics-range-summary">
            <span>{{
              t(`presentationAnalytics.presets.${selectedPreset}`)
            }}</span>
            <strong v-if="selectedPreset !== 'custom'">
              {{ selectedRangeText }}
            </strong>
          </div>

          <div class="presentation-analytics-presets">
            <button
              v-for="preset in ['30d', '90d', 'ytd', '12m', 'custom']"
              :key="preset"
              type="button"
              class="presentation-analytics-preset"
              :class="
                selectedPreset === preset
                  ? 'presentation-analytics-preset--active'
                  : ''
              "
              @click="selectPreset(preset as PresentationAnalyticsPreset)"
            >
              {{ t(`presentationAnalytics.presets.${preset}`) }}
            </button>
          </div>

          <Transition name="presentation-analytics-range">
            <VueDatePicker
              v-if="selectedPreset === 'custom'"
              v-model="customDateRange"
              class="presentation-analytics-date-picker"
              range
              :enable-time-picker="false"
              :locale="dfnsLocale"
              :max-date="new Date()"
              :format="datePickerFormat"
              :preview-format="datePickerFormat"
              :clearable="false"
              auto-apply
              :placeholder="t('presentationAnalytics.customRangePlaceholder')"
            >
              <template #trigger>
                <button
                  type="button"
                  class="presentation-analytics-date-trigger"
                >
                  <span class="material-symbols-outlined">calendar_today</span>
                  <span>{{ selectedRangeText }}</span>
                  <span class="material-symbols-outlined">expand_more</span>
                </button>
              </template>
            </VueDatePicker>
          </Transition>
        </div>
      </div>

      <div class="presentation-analytics-view-switch">
        <button
          type="button"
          class="presentation-analytics-view-button"
          :class="
            selectedView === 'performance'
              ? 'presentation-analytics-view-button--active'
              : ''
          "
          data-test="presentation-analytics-view-performance"
          @click="selectView('performance')"
        >
          <span class="material-symbols-outlined tw:text-lg">insert_chart</span>
          {{ t("presentationAnalytics.views.performance") }}
        </button>

        <button
          type="button"
          class="presentation-analytics-view-button"
          :class="
            selectedView === 'pages'
              ? 'presentation-analytics-view-button--active'
              : ''
          "
          data-test="presentation-analytics-view-pages"
          @click="selectView('pages')"
        >
          <span class="material-symbols-outlined tw:text-lg">view_agenda</span>
          {{ t("presentationAnalytics.views.pages") }}
        </button>
      </div>

      <div
        v-if="isLoading && !analyticsDocument"
        class="tw:flex tw:min-h-[40vh] tw:items-center tw:justify-center"
      >
        <LoadingSpinner :color="'var(--classic-text, black)'" />
      </div>

      <div
        v-else-if="analyticsError || pageDataError"
        class="tw:mt-6 tw:rounded-2xl tw:border tw:border-[var(--classic-warning,var(--tw-color-amber-200))] tw:bg-[var(--classic-warning-surface,var(--tw-color-amber-50))] tw:p-5 tw:text-sm tw:text-[var(--classic-warning,var(--tw-color-amber-900))]"
      >
        {{ t("presentationAnalytics.errors.loadFailed") }}
      </div>

      <template v-else-if="selectedView === 'performance'">
        <div
          class="tw:mt-6 tw:grid tw:grid-cols-1 tw:gap-4 tw:lg:grid-cols-3"
          data-test="hcp-metrics"
        >
          <div
            v-for="metric in ['total', 'withHcp', 'assignments']"
            :key="metric"
            class="tw:rounded-2xl tw:border tw:border-[var(--classic-border-subtle,var(--tw-color-gray-200))] tw:bg-[var(--classic-surface,var(--tw-color-white))] tw:p-5 tw:shadow-[0_16px_48px_-32px_rgba(3,41,71,0.35)]"
          >
            <div
              class="tw:text-sm tw:text-[var(--classic-text-muted,var(--tw-color-gray-500))]"
            >
              {{ t(`presentationAnalytics.hcps.${metric}`) }}
            </div>
            <div
              :data-test="`hcp-metric-${metric}`"
              class="tw:mt-3 tw:text-3xl tw:font-semibold tw:text-[var(--classic-text,var(--tw-color-gray-900))]"
            >
              {{
                metric === "total"
                  ? formatCount(hcpMetrics?.totalPresentations)
                  : metric === "assignments"
                    ? formatCount(hcpMetrics?.totalHcpAssignments)
                    : t("presentationAnalytics.hcps.ratio", {
                        count: formatCount(hcpMetrics?.presentationsWithHcp),
                        total: formatCount(hcpMetrics?.totalPresentations),
                      })
              }}
            </div>
            <p
              class="tw:mt-2 tw:text-xs tw:text-[var(--classic-text-muted,var(--tw-color-gray-500))]"
            >
              {{ t(`presentationAnalytics.hcps.${metric}Hint`) }}
            </p>
          </div>
        </div>
        <div class="tw:mt-6 tw:grid tw:grid-cols-1 tw:gap-4 tw:lg:grid-cols-3">
          <div
            class="tw:rounded-2xl tw:border tw:border-[var(--classic-border-subtle,var(--tw-color-gray-200))] tw:bg-[var(--classic-surface,var(--tw-color-white))] tw:p-5 tw:shadow-[0_16px_48px_-32px_rgba(3,41,71,0.35)]"
          >
            <div
              class="tw:text-sm tw:text-[var(--classic-text-muted,var(--tw-color-gray-500))]"
            >
              {{ t("presentationAnalytics.metrics.views") }}
            </div>
            <div class="tw:mt-3 tw:flex tw:items-end tw:gap-2">
              <span
                class="tw:text-3xl tw:font-semibold tw:text-[var(--classic-text,var(--tw-color-gray-900))]"
              >
                {{ formatViews(performanceSummary.totalViews) }}
              </span>
              <span
                class="tw:pb-1 tw:text-xs tw:font-medium tw:text-[var(--classic-accent,var(--tw-color-pv-turquoise-700))]"
              >
                {{ t("presentationAnalytics.metrics.viewsHint") }}
              </span>
            </div>
          </div>

          <div
            class="tw:rounded-2xl tw:border tw:border-[var(--classic-border-subtle,var(--tw-color-gray-200))] tw:bg-[var(--classic-surface,var(--tw-color-white))] tw:p-5 tw:shadow-[0_16px_48px_-32px_rgba(3,41,71,0.35)]"
          >
            <div
              class="tw:text-sm tw:text-[var(--classic-text-muted,var(--tw-color-gray-500))]"
            >
              {{ t("presentationAnalytics.metrics.avgWatchTime") }}
            </div>
            <div class="tw:mt-3 tw:flex tw:items-end tw:gap-2">
              <span
                class="tw:text-3xl tw:font-semibold tw:text-[var(--classic-text,var(--tw-color-gray-900))]"
              >
                {{
                  formatDuration(
                    performanceSummary.weightedAvgWatchTimeInSeconds,
                  )
                }}
              </span>
              <span
                class="tw:pb-1 tw:text-xs tw:font-medium tw:text-[var(--classic-info,var(--tw-color-pv-navy-700))]"
              >
                {{ t("presentationAnalytics.metrics.avgWatchTimeHint") }}
              </span>
            </div>
          </div>

          <div
            class="tw:rounded-2xl tw:border tw:border-[var(--classic-border-subtle,var(--tw-color-gray-200))] tw:bg-[var(--classic-surface,var(--tw-color-white))] tw:p-5 tw:shadow-[0_16px_48px_-32px_rgba(3,41,71,0.35)]"
          >
            <div
              class="tw:text-sm tw:text-[var(--classic-text-muted,var(--tw-color-gray-500))]"
            >
              {{ t("presentationAnalytics.metrics.activePages") }}
            </div>
            <div class="tw:mt-3 tw:flex tw:items-end tw:gap-2">
              <span
                class="tw:text-3xl tw:font-semibold tw:text-[var(--classic-text,var(--tw-color-gray-900))]"
              >
                {{ performanceSummary.activePages }} /
                {{ performanceSummary.totalPages }}
              </span>
              <span
                class="tw:pb-1 tw:text-xs tw:font-medium tw:text-[var(--classic-text-muted,var(--tw-color-gray-500))]"
              >
                {{ t("presentationAnalytics.metrics.activePagesHint") }}
              </span>
            </div>
          </div>
        </div>

        <div
          class="tw:mt-6 tw:grid tw:grid-cols-1 tw:gap-6 tw:xl:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]"
        >
          <div>
            <div
              class="tw:mb-3 tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-3"
            >
              <div>
                <h3
                  class="tw:text-lg tw:font-semibold tw:text-[var(--classic-text,var(--tw-color-gray-900))]"
                >
                  {{ t("presentationAnalytics.performance.heading") }}
                </h3>
                <p
                  class="tw:mt-1 tw:text-sm tw:text-[var(--classic-text-muted,var(--tw-color-gray-500))]"
                >
                  {{ t("presentationAnalytics.performance.info") }}
                </p>
              </div>

              <div
                class="tw:inline-flex tw:rounded-full tw:bg-[var(--classic-surface,var(--tw-color-white))] tw:p-1 tw:ring-1 tw:ring-[var(--classic-border-subtle,var(--tw-color-gray-200))]"
              >
                <button
                  type="button"
                  class="tw:rounded-full tw:px-3 tw:py-1.5 tw:text-sm tw:font-medium tw:transition-colors"
                  :class="
                    chartMetric === 'views'
                      ? 'tw:bg-[var(--classic-accent,var(--tw-color-pv-turquoise-600))] tw:text-[var(--classic-on-accent,var(--tw-color-white))]'
                      : 'tw:text-[var(--classic-text-secondary,var(--tw-color-gray-600))]'
                  "
                  @click="chartMetric = 'views'"
                >
                  {{ t("presentationAnalytics.metrics.views") }}
                </button>
                <button
                  type="button"
                  class="tw:rounded-full tw:px-3 tw:py-1.5 tw:text-sm tw:font-medium tw:transition-colors"
                  :class="
                    chartMetric === 'watchTime'
                      ? 'tw:bg-[var(--classic-info,var(--tw-color-pv-navy-700))] tw:text-[var(--classic-on-accent,var(--tw-color-white))]'
                      : 'tw:text-[var(--classic-text-secondary,var(--tw-color-gray-600))]'
                  "
                  @click="chartMetric = 'watchTime'"
                >
                  {{ t("presentationAnalytics.metrics.avgWatchTime") }}
                </button>
              </div>
            </div>

            <PresentationAnalyticsChart
              :metric-label="
                chartMetric === 'views'
                  ? t('presentationAnalytics.metrics.views')
                  : t('presentationAnalytics.metrics.avgWatchTime')
              "
              :value-formatter="formatMetricValue"
              :points="chartPoints"
              :empty-label="t('presentationAnalytics.empty.chart')"
            />
          </div>

          <div
            class="tw:rounded-2xl tw:border tw:border-[var(--classic-border-subtle,var(--tw-color-gray-200))] tw:bg-[var(--classic-surface,var(--tw-color-white))] tw:p-5 tw:shadow-[0_16px_48px_-32px_rgba(3,41,71,0.35)]"
          >
            <div class="tw:flex tw:items-center tw:justify-between tw:gap-3">
              <div>
                <h3
                  class="tw:text-lg tw:font-semibold tw:text-[var(--classic-text,var(--tw-color-gray-900))]"
                >
                  {{ t("presentationAnalytics.drilldown.heading") }}
                </h3>
                <p
                  class="tw:mt-1 tw:text-sm tw:text-[var(--classic-text-muted,var(--tw-color-gray-500))]"
                >
                  {{ t("presentationAnalytics.drilldown.info") }}
                </p>
              </div>
            </div>

            <div
              class="tw:mt-4 tw:overflow-hidden tw:rounded-xl tw:border tw:border-[var(--classic-border-subtle,var(--tw-color-gray-200))]"
            >
              <table class="tw:min-w-full tw:border-collapse">
                <thead
                  class="tw:bg-[var(--classic-surface-raised,var(--tw-color-gray-50))]"
                >
                  <tr>
                    <th
                      class="tw:px-4 tw:py-3 tw:text-left tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wide tw:text-[var(--classic-text-muted,var(--tw-color-gray-500))]"
                    >
                      {{ t("presentationAnalytics.drilldown.period") }}
                    </th>
                    <th
                      class="tw:px-4 tw:py-3 tw:text-right tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wide tw:text-[var(--classic-text-muted,var(--tw-color-gray-500))]"
                    >
                      {{ t("presentationAnalytics.metrics.views") }}
                    </th>
                    <th
                      class="tw:px-4 tw:py-3 tw:text-right tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wide tw:text-[var(--classic-text-muted,var(--tw-color-gray-500))]"
                    >
                      {{ t("presentationAnalytics.metrics.avgWatchTime") }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in drilldownRows"
                    :key="row.label"
                    class="tw:border-t tw:border-[var(--classic-border-subtle,var(--tw-color-gray-100))]"
                  >
                    <td
                      class="tw:px-4 tw:py-3 tw:text-sm tw:text-[var(--classic-text-secondary,var(--tw-color-gray-700))]"
                    >
                      {{ row.label }}
                    </td>
                    <td
                      class="tw:px-4 tw:py-3 tw:text-right tw:text-sm tw:font-medium tw:text-[var(--classic-text,var(--tw-color-gray-900))]"
                    >
                      {{ formatViews(row.views) }}
                    </td>
                    <td
                      class="tw:px-4 tw:py-3 tw:text-right tw:text-sm tw:font-medium tw:text-[var(--classic-text,var(--tw-color-gray-900))]"
                    >
                      {{ formatDuration(row.avgWatchTimeInSeconds) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </template>

      <template v-else>
        <div class="tw:mt-6">
          <div class="tw:mb-4">
            <h3
              class="tw:text-lg tw:font-semibold tw:text-[var(--classic-text,var(--tw-color-gray-900))]"
            >
              {{ t("presentationAnalytics.pages.heading") }}
            </h3>
            <p
              class="tw:mt-1 tw:text-sm tw:text-[var(--classic-text-muted,var(--tw-color-gray-500))]"
            >
              {{ t("presentationAnalytics.pages.info") }}
            </p>
          </div>

          <div
            class="tw:overflow-hidden tw:rounded-2xl tw:border tw:border-[var(--classic-border-subtle,var(--tw-color-gray-200))] tw:bg-[var(--classic-surface,var(--tw-color-white))] tw:shadow-[0_16px_48px_-32px_rgba(3,41,71,0.35)]"
          >
            <div class="tw:overflow-x-auto">
              <div
                class="presentation-analytics-page-table-grid tw:items-center tw:gap-3 tw:border-b tw:border-[var(--classic-border-subtle,var(--tw-color-gray-200))] tw:bg-[var(--classic-surface-raised,var(--tw-color-gray-50))] tw:px-5 tw:py-3 tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wide tw:text-[var(--classic-text-muted,var(--tw-color-gray-500))]"
              >
                <button
                  type="button"
                  class="tw:flex tw:items-center tw:justify-center tw:gap-1"
                  @click="toggleSort('pageNumber')"
                >
                  {{ t("presentationAnalytics.pages.columns.page") }}
                  <span class="material-symbols-outlined tw:text-base">
                    {{ sortIcon("pageNumber") }}
                  </span>
                </button>
                <div>
                  {{ t("presentationAnalytics.pages.columns.preview") }}
                </div>
                <button
                  type="button"
                  class="tw:flex tw:items-center tw:justify-end tw:gap-1"
                  @click="toggleSort('views')"
                >
                  {{ t("presentationAnalytics.pages.columns.views") }}
                  <span class="material-symbols-outlined tw:text-base">
                    {{ sortIcon("views") }}
                  </span>
                </button>
                <button
                  type="button"
                  class="tw:flex tw:items-center tw:justify-end tw:gap-1"
                  @click="toggleSort('avgDuration')"
                >
                  {{ t("presentationAnalytics.pages.columns.avgWatchTime") }}
                  <span class="material-symbols-outlined tw:text-base">
                    {{ sortIcon("avgDuration") }}
                  </span>
                </button>
                <div
                  class="presentation-analytics-page-table-attention tw:text-right"
                >
                  {{ t("presentationAnalytics.pages.columns.intensity") }}
                </div>
              </div>

              <div class="tw:max-h-[68vh] tw:overflow-y-auto">
                <div
                  v-for="row in visiblePageRows"
                  :key="row.pageId"
                  class="presentation-analytics-page-table-grid tw:items-center tw:gap-3 tw:border-b tw:border-[var(--classic-border-subtle,var(--tw-color-gray-100))] tw:px-5 tw:py-4 last:tw:border-b-0"
                >
                  <div class="tw:flex tw:items-center tw:justify-center">
                    <div
                      class="tw:inline-flex tw:min-w-[48px] tw:justify-center tw:rounded-2xl tw:px-3 tw:py-2 tw:text-sm tw:font-semibold"
                      :style="pageChipStyle(row)"
                    >
                      {{ row.pageNumber }}
                    </div>
                  </div>

                  <div class="tw:flex tw:items-center tw:gap-5">
                    <div class="presentation-analytics-page-thumbnail">
                      <Thumbnail :pageId="row.pageId" />
                    </div>
                    <div class="tw:min-w-0">
                      <div
                        class="tw:text-sm tw:font-medium tw:text-[var(--classic-text,var(--tw-color-gray-900))]"
                      >
                        {{
                          t("presentationAnalytics.pages.pageLabel", {
                            number: row.pageNumber,
                          })
                        }}
                      </div>
                      <div
                        class="tw:mt-1 tw:text-sm tw:text-[var(--classic-text-muted,var(--tw-color-gray-500))]"
                      >
                        {{
                          row.views > 0
                            ? t("presentationAnalytics.pages.hasQualifiedViews")
                            : t("presentationAnalytics.pages.noQualifiedViews")
                        }}
                      </div>
                    </div>
                  </div>

                  <div
                    class="tw:text-right tw:text-sm tw:font-semibold tw:text-[var(--classic-text,var(--tw-color-gray-900))]"
                  >
                    {{ formatViews(row.views) }}
                  </div>

                  <div
                    class="tw:text-right tw:text-sm tw:font-semibold tw:text-[var(--classic-text,var(--tw-color-gray-900))]"
                  >
                    {{ formatDuration(row.avgDurationSeconds) }}
                  </div>

                  <div
                    class="presentation-analytics-page-table-attention tw:flex tw:justify-end"
                  >
                    <div
                      class="tw:w-full tw:max-w-[180px] tw:rounded-full tw:bg-[var(--classic-surface-inset,var(--tw-color-gray-100))] tw:p-1"
                    >
                      <div
                        class="tw:h-2.5 tw:rounded-full tw:bg-gradient-to-r tw:from-[var(--classic-accent,var(--tw-color-pv-turquoise-400))] tw:to-[var(--classic-info,var(--tw-color-pv-navy-500))]"
                        :style="{ width: pageBarWidth(row) }"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div
                class="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-4 tw:border-t tw:border-[var(--classic-border-subtle,var(--tw-color-gray-200))] tw:bg-[var(--classic-surface-raised,var(--tw-color-gray-50))] tw:px-5 tw:py-4"
              >
                <div class="tw:flex tw:items-center tw:gap-3">
                  <label
                    class="tw:text-sm tw:font-medium tw:text-[var(--classic-text-secondary,var(--tw-color-gray-600))]"
                    for="presentation-analytics-page-size"
                  >
                    {{ t("presentationAnalytics.pages.pagination.rows") }}
                  </label>
                  <select
                    id="presentation-analytics-page-size"
                    v-model.number="pageSize"
                    class="tw:min-w-[88px] tw:rounded-xl tw:border tw:border-[var(--classic-border-subtle,var(--tw-color-gray-200))] tw:bg-[var(--classic-surface,var(--tw-color-white))] tw:px-3 tw:py-2 tw:text-sm tw:font-medium tw:text-[var(--classic-text-secondary,var(--tw-color-gray-700))]"
                    data-test="presentation-analytics-page-size"
                  >
                    <option
                      v-for="option in pageSizeOptions"
                      :key="option"
                      :value="option"
                    >
                      {{ option }}
                    </option>
                  </select>
                </div>

                <div
                  v-if="totalTablePages > 1"
                  class="tw:flex tw:flex-wrap tw:items-center tw:justify-end tw:gap-2"
                >
                  <button
                    type="button"
                    class="presentation-analytics-pagination-button"
                    :disabled="currentTablePage === 1"
                    data-test="presentation-analytics-page-prev"
                    @click="goToTablePage(currentTablePage - 1)"
                  >
                    {{ t("presentationAnalytics.pages.pagination.previous") }}
                  </button>

                  <template
                    v-for="(item, index) in paginationItems"
                    :key="`${item}-${index}`"
                  >
                    <span
                      v-if="item === 'ellipsis'"
                      class="tw:px-1 tw:text-sm tw:text-[var(--classic-text-muted,var(--tw-color-gray-400))]"
                    >
                      &hellip;
                    </span>
                    <button
                      v-else
                      type="button"
                      class="presentation-analytics-pagination-button"
                      :class="
                        currentTablePage === item
                          ? 'presentation-analytics-pagination-button--active'
                          : ''
                      "
                      :data-test="`presentation-analytics-page-${item}`"
                      @click="goToTablePage(item)"
                    >
                      {{ item }}
                    </button>
                  </template>

                  <button
                    type="button"
                    class="presentation-analytics-pagination-button"
                    :disabled="currentTablePage === totalTablePages"
                    data-test="presentation-analytics-page-next"
                    @click="goToTablePage(currentTablePage + 1)"
                  >
                    {{ t("presentationAnalytics.pages.pagination.next") }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>

  <div
    v-else-if="isLoading"
    class="tw:font-sans tw:-mx-6 tw:-mb-6 tw:flex tw:min-h-[50vh] tw:items-center tw:justify-center tw:bg-[var(--classic-surface-raised,var(--tw-color-gray-50))]"
  >
    <LoadingSpinner :color="'var(--classic-text, black)'" />
  </div>

  <div
    v-else
    class="tw:font-sans tw:-mx-6 tw:-mb-6 tw:bg-[var(--classic-surface-raised,var(--tw-color-gray-50))]"
  >
    <div
      class="tw:mx-auto tw:flex tw:min-h-[50vh] tw:max-w-[720px] tw:flex-col tw:items-center tw:justify-center tw:px-6 tw:py-10 tw:text-center"
    >
      <div
        class="tw:rounded-3xl tw:bg-[var(--classic-surface,var(--tw-color-white))] tw:p-8 tw:shadow-[0_16px_48px_-32px_rgba(3,41,71,0.35)]"
      >
        <div
          class="tw:text-[0.72rem] tw:font-semibold tw:uppercase tw:tracking-[0.2em] tw:text-[var(--classic-text-muted,var(--tw-color-gray-400))]"
        >
          {{ t("presentationAnalytics.title") }}
        </div>
        <h3
          class="tw:mt-3 tw:text-2xl tw:font-semibold tw:text-[var(--classic-text,var(--tw-color-gray-900))]"
        >
          {{ t("presentationAnalytics.errors.unavailableTitle") }}
        </h3>
        <p
          class="tw:mt-3 tw:max-w-[34rem] tw:text-sm tw:leading-6 tw:text-[var(--classic-text-muted,var(--tw-color-gray-500))]"
        >
          {{ t("presentationAnalytics.errors.unavailableInfo") }}
        </p>
        <button
          type="button"
          class="tw:mt-6 tw:inline-flex tw:items-center tw:justify-center tw:rounded-full tw:bg-[var(--classic-info,var(--tw-color-pv-navy-700))] tw:px-4 tw:py-2 tw:text-sm tw:font-medium tw:text-[var(--classic-on-accent,var(--tw-color-white))]"
          @click="
            router.replace({
              name: 'DocumentProperties',
              params: route.params,
            })
          "
        >
          {{ t("presentationAnalytics.errors.backToDocument") }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.presentation-analytics-page {
  min-height: calc(100vh - 72px);
  background:
    radial-gradient(
      circle at 16% 0%,
      rgba(53, 182, 185, 0.12),
      transparent 30rem
    ),
    linear-gradient(
      180deg,
      var(--classic-page, #fbfcfc) 0%,
      var(--classic-page, #f5f7f8) 100%
    );
}

.presentation-analytics-container {
  width: min(100%, 1220px);
  margin: 0 auto;
  padding: 2rem 2rem 2.5rem;
}

.presentation-analytics-hero {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(340px, 430px);
  gap: 2rem;
  align-items: end;
  padding: 2rem;
  border: 1px solid var(--classic-border-subtle, rgba(203, 213, 225, 0.74));
  border-radius: 2rem;
  background:
    linear-gradient(
      135deg,
      var(--classic-surface, rgba(255, 255, 255, 0.95)),
      var(--classic-surface, rgba(255, 255, 255, 0.82))
    ),
    linear-gradient(135deg, rgba(1, 55, 68, 0.08), rgba(53, 182, 185, 0.08));
  box-shadow: 0 24px 70px -50px rgba(3, 41, 71, 0.65);
}

.presentation-analytics-hero-copy {
  min-width: 0;
}

.presentation-analytics-eyebrow {
  color: var(--classic-text-muted, #8a96a6);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
}

.presentation-analytics-title {
  max-width: 680px;
  margin: 0.8rem 0 0;
  color: var(--classic-text, #111827);
  font-size: clamp(1.55rem, 2.15vw, 2.35rem);
  font-weight: 650;
  line-height: 1.08;
  letter-spacing: 0.005em;
  overflow-wrap: anywhere;
}

.presentation-analytics-subtitle {
  max-width: 720px;
  margin: 1.35rem 0 0;
  color: var(--classic-text-muted, #677384);
  font-size: 1rem;
  line-height: 1.7;
}

.presentation-analytics-range-panel {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  width: 100%;
  padding: 1rem;
  border: 1px solid var(--classic-border-subtle, rgba(203, 213, 225, 0.8));
  border-radius: 1.5rem;
  background: var(--classic-surface, rgba(255, 255, 255, 0.78));
  box-shadow: 0 18px 50px -42px rgba(3, 41, 71, 0.75);
  backdrop-filter: blur(12px);
}

.presentation-analytics-label-filter {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
  font-size: 0.875rem;
}
.presentation-analytics-label-filter select {
  font: inherit;
  color: var(--classic-text, #111827);
  background: var(--classic-surface, #fff);
  border: 1px solid var(--classic-border-subtle, #d1d5db);
  appearance: auto;
  border-radius: 0.5rem;
  padding: 0.5rem;
}
.presentation-analytics-range-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  color: var(--classic-text-muted, #697586);
  font-size: 0.82rem;
}

.presentation-analytics-range-summary span {
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

.presentation-analytics-range-summary strong {
  color: var(--classic-text, #1f2937);
  font-size: 0.88rem;
  font-weight: 650;
  white-space: nowrap;
}

.presentation-analytics-presets {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 0.35rem;
  padding: 0.35rem;
  border: 1px solid var(--classic-border-subtle, #e5e7eb);
  border-radius: 999px;
  background: var(--classic-surface-raised, #f8fafc);
}

.presentation-analytics-preset {
  min-height: 2.35rem;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: var(--classic-text-secondary, #4b5563);
  font-size: 0.88rem;
  font-weight: 650;
  line-height: 1.15;
  transition:
    background 160ms ease,
    box-shadow 160ms ease,
    color 160ms ease,
    transform 160ms ease;
}

.presentation-analytics-preset:hover {
  color: var(--classic-text, #111827);
  background: var(--classic-surface-hover, rgba(255, 255, 255, 0.72));
}

.presentation-analytics-preset--active {
  color: var(--classic-accent, #005a5f);
  background: var(--classic-surface, #ffffff);
  box-shadow:
    0 12px 22px -18px rgba(3, 41, 71, 0.75),
    inset 0 0 0 1px rgba(53, 182, 185, 0.34);
}

.presentation-analytics-view-switch {
  display: inline-flex;
  gap: 0.25rem;
  margin-top: 1.25rem;
  padding: 0.35rem;
  border: 1px solid var(--classic-border-subtle, #e5e7eb);
  border-radius: 999px;
  background: var(--classic-surface, rgba(255, 255, 255, 0.8));
  box-shadow: 0 18px 50px -42px rgba(3, 41, 71, 0.75);
}

.presentation-analytics-view-button {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  min-height: 2.6rem;
  border: 0;
  border-radius: 999px;
  padding: 0 1rem;
  background: transparent;
  color: var(--classic-text-secondary, #556172);
  font-size: 0.92rem;
  font-weight: 700;
  transition:
    background 160ms ease,
    color 160ms ease,
    box-shadow 160ms ease;
}

.presentation-analytics-view-button:hover {
  color: var(--classic-text, #111827);
  background: var(--classic-surface-hover, rgba(248, 250, 252, 0.92));
}

.presentation-analytics-view-button--active {
  color: var(--classic-on-accent, #ffffff);
  background: linear-gradient(
    135deg,
    var(--classic-accent, #138c92),
    var(--classic-accent, #00747d)
  );
  box-shadow: 0 15px 28px -20px rgba(0, 116, 125, 0.9);
}

.presentation-analytics-range-enter-active,
.presentation-analytics-range-leave-active {
  transition:
    opacity 160ms ease,
    transform 160ms ease;
}

.presentation-analytics-range-enter-from,
.presentation-analytics-range-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

:deep(.presentation-analytics-date-picker) {
  width: 100%;
}

.presentation-analytics-date-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
  min-height: 2.65rem;
  border: 1px solid var(--classic-border, #c9d5df);
  border-radius: 1rem;
  padding: 0 0.85rem;
  color: var(--classic-text, #1f2937);
  background: var(--classic-surface, #ffffff);
  font-size: 0.9rem;
  font-weight: 700;
  text-align: left;
  box-shadow: 0 12px 28px -24px rgba(3, 41, 71, 0.7);
  transition:
    border-color 160ms ease,
    box-shadow 160ms ease,
    transform 160ms ease;
}

.presentation-analytics-date-trigger:hover {
  border-color: var(--classic-accent, #35b6b9);
  box-shadow:
    0 16px 34px -25px rgba(3, 41, 71, 0.75),
    0 0 0 3px rgba(53, 182, 185, 0.11);
}

.presentation-analytics-date-trigger span:nth-child(2) {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.presentation-analytics-date-trigger .material-symbols-outlined {
  color: var(--classic-text-muted, #667085);
  font-size: 1.05rem;
  flex: 0 0 auto;
}

.presentation-analytics-pagination-button {
  min-height: 2.2rem;
  min-width: 2.2rem;
  border: 1px solid var(--classic-border, #d7dee6);
  border-radius: 999px;
  padding: 0 0.85rem;
  color: var(--classic-text-secondary, #4b5563);
  background: var(--classic-surface, #ffffff);
  font-size: 0.88rem;
  font-weight: 650;
  transition:
    border-color 160ms ease,
    background 160ms ease,
    color 160ms ease;
}

.presentation-analytics-pagination-button:hover:not(:disabled) {
  border-color: var(--classic-accent, #35b6b9);
  color: var(--classic-text, #0f172a);
  background: var(--classic-surface-hover, #f8fbfb);
}

.presentation-analytics-pagination-button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.presentation-analytics-pagination-button--active {
  border-color: var(--classic-accent, #0f766e);
  color: var(--classic-on-accent, #ffffff);
  background: linear-gradient(
    135deg,
    var(--classic-accent, #138c92),
    var(--classic-accent, #00747d)
  );
}

.presentation-analytics-page-table-grid {
  display: grid;
  grid-template-columns: 84px minmax(260px, 1fr) 120px 150px minmax(
      160px,
      200px
    );
}

.presentation-analytics-page-thumbnail {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 5.5rem;
  height: 6.5rem;
  overflow: hidden;
  border: 1px solid var(--classic-border-subtle, #e5e7eb);
  border-radius: 0.85rem;
  background: var(--classic-surface-raised, #f8fafc);
}

.presentation-analytics-page-thumbnail :deep(.thumbnail) {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.presentation-analytics-page-thumbnail :deep(img) {
  display: block;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

:deep(.presentation-analytics-date-picker .dp__input_wrap) {
  width: 100%;
}

:deep(.presentation-analytics-date-picker .dp__input) {
  border: 1px solid var(--classic-border, #c9d5df);
  border-radius: 1rem;
  min-height: 42px;
  padding: 0 1rem 0 2.35rem;
  color: var(--classic-text, #111827);
  background: var(--classic-surface, #ffffff);
  font-size: 0.92rem;
  font-weight: 600;
  box-shadow: none;
}

:deep(.presentation-analytics-date-picker .dp__input:focus) {
  border-color: var(--classic-accent, #35b6b9);
  box-shadow: 0 0 0 3px rgba(53, 182, 185, 0.14);
}

:deep(.presentation-analytics-date-picker .dp__input_icon) {
  color: var(--classic-text-muted, #667085);
  inset-inline-start: 0.8rem;
}

:deep(.presentation-analytics-date-picker .dp__theme_light) {
  --dp-primary-color: var(--classic-accent, #35b6b9);
  --dp-primary-disabled-color: var(--classic-control-disabled, #e6f7f7);
  --dp-hover-color: var(--classic-surface-hover, #f3f4f6);
  --dp-border-color: var(--classic-border-subtle, #e5e7eb);
  --dp-background-color: var(--classic-surface, #ffffff);
  --dp-menu-border-color: var(--classic-border-subtle, #e5e7eb);
}

@media (max-width: 1280px) {
  .presentation-analytics-page-table-grid {
    grid-template-columns: 84px minmax(240px, 1fr) 110px 140px;
  }

  .presentation-analytics-page-table-attention {
    display: none;
  }
}

@media (max-width: 960px) {
  .presentation-analytics-hero {
    grid-template-columns: 1fr;
    align-items: start;
  }

  .presentation-analytics-range-panel {
    max-width: 100%;
  }
}

@media (max-width: 680px) {
  .presentation-analytics-container {
    padding: 1rem 1rem 2rem;
  }

  .presentation-analytics-hero {
    gap: 1.4rem;
    padding: 1.25rem;
    border-radius: 1.5rem;
  }

  .presentation-analytics-title {
    font-size: clamp(1.45rem, 8vw, 2rem);
  }

  .presentation-analytics-presets {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    border-radius: 1.25rem;
  }

  .presentation-analytics-preset {
    border-radius: 1rem;
  }

  .presentation-analytics-range-summary {
    align-items: flex-start;
    flex-direction: column;
    gap: 0.35rem;
  }

  .presentation-analytics-range-summary strong {
    white-space: normal;
  }

  .presentation-analytics-view-switch {
    display: grid;
    grid-template-columns: 1fr;
    width: 100%;
    border-radius: 1.35rem;
  }

  .presentation-analytics-view-button {
    justify-content: center;
  }
}
</style>
